import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { Images } from '../../../../../utils/imageConstants';
import { resolvePublicImageUrl } from '../../../../../helper/helper';
import { fetchDivisions } from '../../../../myTeam/api/myTeamApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEY } from '../../../../../services/queryEndPoint';
import { Api } from '../../../../../services/interceptor';
import {
  scheduleMatchRequest,
  buildUtcMatchTimeFromDateAndTime,
  getScheduleMatchRequestErrorMessage,
} from '../../../../myLeagues/api/myLeaguesApi';
import { findDivisionGroupForTeamIds, type TeamOption } from './TeamSelectModal';
import TickerAutomationModal from './TickerAutomationModal';
import MaterialTimePicker from '../../../../myLeagues/components/MaterialTimePicker';
import {
  normalizePitchTypeForSchedule,
  normalizePlayoffTypeForSchedule,
  PITCH_TYPE_OPTIONS,
  PLAYOFF_TYPE_OPTIONS,
} from './scheduleMatchEnums';
import { getTournamentOversForSchedule } from './scheduleTournamentContext';
import type { ITournamentsByLeagueResponse } from '../../../types/home';
import FormDropdown from '../../../../../components/common/FormDropdown';
import { ProfileDatePicker } from '../../../../profileOnboarding/components/ProfileDatePicker';
import {
  extractGroundIdForSchedule,
  localDateAndTimeFromIso,
  officialScheduleId,
  resolveDivisionSelectionFromApi,
} from './scheduleMatchPrefillUtils';
import type { IMatchDetails } from './matchesDetails/types/matchDetails.d';
import './ScheduleMatchModal.scss';

type GroundOption = { groundId: string; groundName: string };
type PersonOption = { id: string; name: string };

export interface EditMatchScheduleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matchId: string | null;
  tournamentId: string | null;
  leagueId: string | null;
  matchType?: string;
  tournamentCategory?: string;
}

function teamLogoSrc(team: TeamOption | null | undefined): string {
  const url = resolvePublicImageUrl(team?.imageUrl ?? null);
  return url || Images.team_logo;
}

function unwrapPersonArray(data: unknown): PersonOption[] {
  const raw = Array.isArray(data)
    ? data
    : data && typeof data === 'object' && 'data' in data && Array.isArray((data as { data: unknown }).data)
      ? (data as { data: unknown[] }).data
      : [];
  return (raw as Record<string, unknown>[])
    .map((u) => ({
      id: String(u.umpireId ?? u.scorerId ?? u.playerId ?? ''),
      name: String(u.name ?? u.umpireName ?? u.scorerName ?? u.playerFirstName ?? ''),
    }))
    .filter((p) => p.id);
}

function resetDetailsFormState(setters: {
  setTeam1: (v: TeamOption | null) => void;
  setTeam2: (v: TeamOption | null) => void;
  setDraftMatchId: (v: string | null) => void;
  setPitchType: (v: string) => void;
  setPlayoffType: (v: string) => void;
  setScorer: (v: string) => void;
  setUmpire1: (v: string) => void;
  setUmpire2: (v: string) => void;
  setUmpire3: (v: string) => void;
  setCity: (v: string) => void;
  setMatchDate: (v: string) => void;
  setStartTime: (v: string) => void;
  setGroundId: (v: string) => void;
  setDivisionId: (v: string) => void;
  setGroupId: (v: string) => void;
  setTickerModalOpen: (v: boolean) => void;
}) {
  setters.setTeam1(null);
  setters.setTeam2(null);
  setters.setDraftMatchId(null);
  setters.setPitchType('');
  setters.setPlayoffType('');
  setters.setScorer('');
  setters.setUmpire1('');
  setters.setUmpire2('');
  setters.setUmpire3('');
  setters.setCity('');
  setters.setMatchDate('');
  setters.setStartTime('');
  setters.setGroundId('');
  setters.setDivisionId('');
  setters.setGroupId('');
  setters.setTickerModalOpen(false);
}

export default function EditMatchScheduleModal({
  open,
  onOpenChange,
  matchId,
  tournamentId,
  leagueId,
  matchType = '',
  tournamentCategory = '',
}: EditMatchScheduleModalProps) {
  const activeMatchId = open && matchId?.trim() ? matchId.trim() : null;
  const queryClient = useQueryClient();

  const divisionLeafFallbackRef = useRef('');
  const prefillAppliedRef = useRef(false);

  const [divisionId, setDivisionId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [team1, setTeam1] = useState<TeamOption | null>(null);
  const [team2, setTeam2] = useState<TeamOption | null>(null);
  const [draftMatchId, setDraftMatchId] = useState<string | null>(null);
  const [pitchType, setPitchType] = useState('');
  const [playoffType, setPlayoffType] = useState('');
  const [scorer, setScorer] = useState('');
  const [umpire1, setUmpire1] = useState('');
  const [umpire2, setUmpire2] = useState('');
  const [umpire3, setUmpire3] = useState('');
  const [city, setCity] = useState('');
  const [matchDate, setMatchDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [groundId, setGroundId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [tickerModalOpen, setTickerModalOpen] = useState(false);
  const [editPrefilled, setEditPrefilled] = useState(false);

  const { data: tournamentsByLeague } = useQuery({
    queryKey: [QUERY_KEY.getTournamentsByLeagues, leagueId],
    queryFn: async () => {
      if (!leagueId) return null;
      const res = await Api.get<ITournamentsByLeagueResponse>(
        `${QUERY_KEY.getTournamentsByLeagues}/${leagueId}`,
      );
      return res?.data ?? null;
    },
    enabled: open && !!leagueId,
  });

  const scheduleTournamentRow = useMemo(() => {
    if (!tournamentId?.trim() || !tournamentsByLeague?.tournamentResponses?.length) return null;
    return (
      tournamentsByLeague.tournamentResponses.find((t) => t.tournamentId === tournamentId) ?? null
    );
  }, [tournamentId, tournamentsByLeague]);

  const { matchOvers: tournamentMatchOversStr, maxOversPerBowler: tournamentMaxBowlerStr } =
    getTournamentOversForSchedule(scheduleTournamentRow);

  const { data: divisionsPayload } = useQuery({
    queryKey: [QUERY_KEY.getDivisions, tournamentId],
    queryFn: () =>
      tournamentId
        ? fetchDivisions(tournamentId)
        : Promise.resolve({ divisionResponseList: [] }),
    enabled: open && !!tournamentId,
  });

  const { data: groundsRaw, isFetched: groundsFetched } = useQuery({
    queryKey: [QUERY_KEY.groundsByLeagueId, leagueId],
    queryFn: async () => {
      if (!leagueId) return [];
      const res = await Api.get<GroundOption[]>(`${QUERY_KEY.groundsByLeagueId}/${leagueId}`);
      return Array.isArray(res?.data) ? res.data : [];
    },
    enabled: open && !!leagueId,
  });
  const grounds: GroundOption[] = (groundsRaw ?? []) as GroundOption[];
  const groundsReady = !leagueId || groundsFetched;

  const { data: umpiresRaw } = useQuery({
    queryKey: [QUERY_KEY.getAvailableUmpires, tournamentId],
    queryFn: async () => {
      if (!tournamentId) return [];
      const res = await Api.get(`${QUERY_KEY.getAvailableUmpires}/${tournamentId}`);
      return res?.data;
    },
    enabled: open && !!tournamentId,
  });
  const umpires: PersonOption[] = unwrapPersonArray(umpiresRaw);

  const { data: scorersRaw } = useQuery({
    queryKey: [QUERY_KEY.getAvailableScorers, tournamentId],
    queryFn: async () => {
      if (!tournamentId) return [];
      const res = await Api.get(`${QUERY_KEY.getAvailableScorers}/${tournamentId}`);
      return res?.data;
    },
    enabled: open && !!tournamentId,
  });
  const scorers: PersonOption[] = unwrapPersonArray(scorersRaw);

  const {
    data: editMatchDetails,
    isLoading: editMatchLoading,
    isError: editMatchError,
    isFetching: editMatchFetching,
  } = useQuery({
    queryKey: [QUERY_KEY.getMatchInfo, activeMatchId, 'edit-schedule-modal'],
    queryFn: async () => {
      const res = await Api.get<IMatchDetails>(`${QUERY_KEY.getMatchInfo}/${activeMatchId}`);
      return res.data;
    },
    enabled: !!activeMatchId,
    staleTime: 0,
    gcTime: 0,
  });

  const divisions = divisionsPayload?.divisionResponseList ?? [];

  // Matches are stored against the parent division, never the group.
  const effectiveDivisionId = divisionId.trim();

  const pitchTypeDropdownOptions = useMemo(
    () => [
      { value: '', label: 'Select pitch type' },
      ...PITCH_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
    ],
    [],
  );

  const playoffDropdownOptions = useMemo(
    () => [
      { value: '', label: 'Select playoff type' },
      ...PLAYOFF_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
    ],
    [],
  );

  const groundDropdownOptions = useMemo(
    () => [
      { value: '', label: 'Select ground' },
      ...grounds.map((g) => ({ value: g.groundId, label: g.groundName })),
    ],
    [grounds],
  );

  const umpireDropdownOptions = useMemo(
    () => [
      { value: '', label: 'Select umpire' },
      ...umpires.map((u) => ({ value: u.id, label: u.name })),
    ],
    [umpires],
  );

  const umpire2DropdownOptions = useMemo(
    () => [
      { value: '', label: 'Select umpire' },
      ...umpires.filter((u) => u.id !== umpire1).map((u) => ({ value: u.id, label: u.name })),
    ],
    [umpires, umpire1],
  );

  const umpire3DropdownOptions = useMemo(
    () => [
      { value: '', label: 'Select umpire' },
      ...umpires
        .filter((u) => u.id !== umpire1 && u.id !== umpire2)
        .map((u) => ({ value: u.id, label: u.name })),
    ],
    [umpires, umpire1, umpire2],
  );

  const scorerDropdownOptions = useMemo(
    () => [
      { value: '', label: 'Select scorer' },
      ...scorers.map((s) => ({ value: s.id, label: s.name })),
    ],
    [scorers],
  );

  useEffect(() => {
    if (!open || !activeMatchId) {
      prefillAppliedRef.current = false;
      divisionLeafFallbackRef.current = '';
      setEditPrefilled(false);
      resetDetailsFormState({
        setTeam1,
        setTeam2,
        setDraftMatchId,
        setPitchType,
        setPlayoffType,
        setScorer,
        setUmpire1,
        setUmpire2,
        setUmpire3,
        setCity,
        setMatchDate,
        setStartTime,
        setGroundId,
        setDivisionId,
        setGroupId,
        setTickerModalOpen,
      });
      return;
    }
    setEditPrefilled(false);
    prefillAppliedRef.current = false;
    divisionLeafFallbackRef.current = '';
  }, [open, activeMatchId]);

  useEffect(() => {
    if (!activeMatchId || !editMatchDetails || prefillAppliedRef.current || !groundsReady) return;

    const st = (editMatchDetails.matchStatus ?? '').toUpperCase().replace(/\s+/g, '_');
    const cannotEditFromHere =
      st === 'ENDED' || st === 'COMPLETED' || st === 'FINISHED' || st === 'IN_PROGRESS';
    if (cannotEditFromHere) {
      prefillAppliedRef.current = true;
      toast.error('Only upcoming / scheduled fixtures can be edited here.');
      onOpenChange(false);
      return;
    }

    const m = editMatchDetails;
    const rawDiv = (m.divisionId ?? '').toString().trim();
    divisionLeafFallbackRef.current = '';

    let resolved = rawDiv ? resolveDivisionSelectionFromApi(rawDiv, divisions) : null;
    if (!resolved) {
      resolved = findDivisionGroupForTeamIds(
        divisions,
        m.teamOne?.teamId ?? '',
        m.teamTwo?.teamId ?? '',
      );
    }
    if (resolved) {
      setDivisionId(resolved.divisionId);
      setGroupId(resolved.groupId);
    } else if (rawDiv) {
      divisionLeafFallbackRef.current = rawDiv;
    }

    setTeam1({
      teamId: m.teamOne.teamId,
      teamName: m.teamOne.teamName,
      teamSnapshotId: m.teamOne.teamSnapshotId ?? '',
      imageUrl: null,
    });
    setTeam2({
      teamId: m.teamTwo.teamId,
      teamName: m.teamTwo.teamName,
      teamSnapshotId: m.teamTwo.teamSnapshotId ?? '',
      imageUrl: null,
    });

    setDraftMatchId(activeMatchId);
    setCity((m.city ?? '').trim());
    const { date, time } = localDateAndTimeFromIso(m.startDate);
    setMatchDate(date);
    setStartTime(time);

    setPitchType(m.pitchType ? normalizePitchTypeForSchedule(m.pitchType) : '');
    setPlayoffType(m.playoffType ? normalizePlayoffTypeForSchedule(m.playoffType) : '');

    setUmpire1(officialScheduleId(m.umpire ?? null));
    setUmpire2(officialScheduleId(m.umpireTwo ?? null));
    setUmpire3(officialScheduleId(m.thirdUmpire ?? null));
    setScorer(officialScheduleId(m.scorer ?? null));

    setGroundId(extractGroundIdForSchedule(m.groundId, grounds));

    prefillAppliedRef.current = true;
    setEditPrefilled(true);
  }, [
    activeMatchId,
    editMatchDetails,
    divisions,
    grounds,
    groundsReady,
    onOpenChange,
  ]);

  const handleHeaderBack = () => {
    onOpenChange(false);
  };

  const handleSave = async () => {
    if (!draftMatchId) {
      toast.error('Match is missing.');
      return;
    }
    if (!city.trim()) {
      toast.error('City is required.');
      return;
    }
    if (!matchDate) {
      toast.error('Match date is required.');
      return;
    }
    if (!startTime) {
      toast.error('Start time is required.');
      return;
    }
    const leafDivision =
      effectiveDivisionId || divisionLeafFallbackRef.current.trim();
    if (!leafDivision) {
      toast.error('Select a division (and group if applicable).');
      return;
    }
    const startDateIso = buildUtcMatchTimeFromDateAndTime(matchDate, startTime);
    if (!startDateIso) {
      toast.error('Invalid date or time.');
      return;
    }
    const u1 = umpire1.trim();
    const u2 = umpire2.trim();
    const u3 = umpire3.trim();
    const scorerId = scorer.trim();
    const parseOptInt = (s: string): number | undefined => {
      const t = s.trim();
      if (!t) return undefined;
      const n = parseInt(t, 10);
      return Number.isFinite(n) ? n : undefined;
    };

    setIsSaving(true);
    try {
      await scheduleMatchRequest({
        matchId: draftMatchId,
        startDate: startDateIso,
        city: city.trim(),
        divisionId: leafDivision,
        pitchType: pitchType || undefined,
        playoffType: playoffType || undefined,
        groundId: groundId.trim() || undefined,
        matchType: matchType || undefined,
        tournamentCategory: tournamentCategory || undefined,
        matchOvers: parseOptInt(tournamentMatchOversStr),
        maxOversPerBowler: parseOptInt(tournamentMaxBowlerStr),
        umpireId: u1 || undefined,
        isUmpire: u1 ? true : undefined,
        umpireTwoId: u2 || undefined,
        isUmpireTwoPerson: u2 ? true : undefined,
        thirdUmpireId: u3 || undefined,
        isThirdUmpirePerson: u3 ? true : undefined,
        scorerId: scorerId || undefined,
        isScorer: scorerId ? true : undefined,
      });
      toast.success('Match updated.');
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.matchByTournamentIdAndStatusPageable] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.matchByTeamAndStatus] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getMatchInfo, draftMatchId] });
      onOpenChange(false);
    } catch (err) {
      console.error('scheduleMatchRequest failed:', err);
      toast.error(
        getScheduleMatchRequestErrorMessage(err, 'Failed to update match.'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const showSpinner =
    !!activeMatchId &&
    !editMatchError &&
    (editMatchLoading || editMatchFetching || !editMatchDetails || !editPrefilled);

  const dialogOpen = Boolean(open && activeMatchId);

  useEffect(() => {
    if (!dialogOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [dialogOpen]);

  useEffect(() => {
    if (!dialogOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dialogOpen, onOpenChange]);

  const modalLayer =
    dialogOpen && typeof document !== 'undefined'
      ? createPortal(
      <div className="edit-match-schedule-layer">
        <button
          type="button"
          className="edit-match-schedule-backdrop"
          aria-label="Close"
          onClick={() => onOpenChange(false)}
        />
        <div
          className="edit-match-schedule-panel schedule-match-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-match-schedule-title"
        >
        <div className="schedule-match-modal">
          <header className="schedule-match-header">
            <button
              type="button"
              className="schedule-match-back"
              onClick={handleHeaderBack}
              aria-label="Close"
            >
              <img src={Images.previous_icon} alt="" />
            </button>
            <h2 id="edit-match-schedule-title" className="schedule-match-title">
              Edit Match
            </h2>
          </header>

          {editMatchError && !editMatchFetching ? (
            <div className="schedule-match-placeholder" role="alert">
              <p>Could not load this match. Check your connection or try again.</p>
              <button
                type="button"
                className="schedule-match-btn schedule-match-btn--next"
                onClick={handleHeaderBack}
              >
                Close
              </button>
            </div>
          ) : showSpinner ? (
            <p className="schedule-match-placeholder" role="status">
              Loading match…
            </p>
          ) : (
            <div className="schedule-match-single">
              <div className="schedule-match-team-pick-row schedule-match-teams--preview">
                <div className="schedule-match-team-preview">
                  <div className="schedule-match-team-preview-logo">
                    <img src={teamLogoSrc(team1)} alt="" />
                  </div>
                  <span className="schedule-match-team-preview-name">{team1?.teamName ?? 'Team 1'}</span>
                </div>
                <div className="schedule-match-vs schedule-match-vs--between" aria-hidden>
                  vs
                </div>
                <div className="schedule-match-team-preview">
                  <div className="schedule-match-team-preview-logo">
                    <img src={teamLogoSrc(team2)} alt="" />
                  </div>
                  <span className="schedule-match-team-preview-name">{team2?.teamName ?? 'Team 2'}</span>
                </div>
              </div>

              <div className="schedule-match-details schedule-match-details--panel">
                <div className="schedule-match-details-row">
                  <div className="schedule-match-field">
                    <label>Match Type</label>
                    <input
                      type="text"
                      value={matchType || '—'}
                      readOnly
                      className="schedule-match-readonly"
                    />
                  </div>
                  <div className="schedule-match-field">
                    <label>Tournament Category</label>
                    <input
                      type="text"
                      value={tournamentCategory || '—'}
                      readOnly
                      className="schedule-match-readonly"
                    />
                  </div>
                  <div className="schedule-match-field">
                    <label>Pitch Type</label>
                    <FormDropdown
                      id="edit-match-modal-pitch-type"
                      value={pitchType}
                      onChange={setPitchType}
                      options={pitchTypeDropdownOptions}
                      placeholder="Select pitch type"
                    />
                  </div>
                </div>
                <div className="schedule-match-details-row">
                  <div className="schedule-match-field">
                    <label>Playoff Type</label>
                    <FormDropdown
                      id="edit-match-modal-playoff-type"
                      value={playoffType}
                      onChange={setPlayoffType}
                      options={playoffDropdownOptions}
                      placeholder="Select playoff type"
                    />
                  </div>
                  <div className="schedule-match-field">
                    <label>Ground</label>
                    <FormDropdown
                      id="edit-match-modal-ground"
                      value={groundId}
                      onChange={setGroundId}
                      options={groundDropdownOptions}
                      placeholder="Select ground"
                    />
                  </div>
                  <div className="schedule-match-field">
                    <label>Match Overs</label>
                    <input
                      type="text"
                      value={tournamentMatchOversStr || '—'}
                      readOnly
                      className="schedule-match-readonly"
                      title="From tournament settings"
                    />
                  </div>
                </div>
                <div className="schedule-match-details-row">
                  <div className="schedule-match-field">
                    <label>Max Overs Per Bowler</label>
                    <input
                      type="text"
                      value={tournamentMaxBowlerStr || '—'}
                      readOnly
                      className="schedule-match-readonly"
                      title="From tournament settings"
                    />
                  </div>
                  <div className="schedule-match-field">
                    <label>
                      City <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="City Name"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                  </div>
                  <div className="schedule-match-field schedule-match-field--date">
                    <label htmlFor="edit-match-modal-match-date">
                      Match Date <span className="req">*</span>
                    </label>
                    <ProfileDatePicker
                      id="edit-match-modal-match-date"
                      value={matchDate}
                      onChange={setMatchDate}
                      placeholder="Select date"
                      disablePast={false}
                    />
                  </div>
                </div>
                <div className="schedule-match-details-row">
                  <div className="schedule-match-field">
                    <label>
                      Start Time <span className="req">*</span>
                    </label>
                    <MaterialTimePicker
                      value={startTime}
                      onChange={setStartTime}
                      placeholder="Select time"
                    />
                  </div>
                  <div className="schedule-match-field">
                    <label>Umpire 1</label>
                    <FormDropdown
                      id="edit-match-modal-umpire-1"
                      value={umpire1}
                      onChange={setUmpire1}
                      options={umpireDropdownOptions}
                      placeholder="Select umpire"
                    />
                  </div>
                  <div className="schedule-match-field">
                    <label>Umpire 2</label>
                    <FormDropdown
                      id="edit-match-modal-umpire-2"
                      value={umpire2}
                      onChange={setUmpire2}
                      options={umpire2DropdownOptions}
                      placeholder="Select umpire"
                    />
                  </div>
                </div>
                <div className="schedule-match-details-row">
                  <div className="schedule-match-field">
                    <label>Third Umpire</label>
                    <FormDropdown
                      id="edit-match-modal-umpire-3"
                      value={umpire3}
                      onChange={setUmpire3}
                      options={umpire3DropdownOptions}
                      placeholder="Select umpire"
                    />
                  </div>
                  <div className="schedule-match-field">
                    <label>Scorer</label>
                    <FormDropdown
                      id="edit-match-modal-scorer"
                      value={scorer}
                      onChange={setScorer}
                      options={scorerDropdownOptions}
                      placeholder="Select scorer"
                    />
                  </div>
                </div>
              </div>

              <div className="schedule-match-ticker">
                <span>Ticker Automation</span>
                <button
                  type="button"
                  className="schedule-match-setup-link"
                  onClick={() => setTickerModalOpen(true)}
                  disabled={!draftMatchId}
                  title={!draftMatchId ? 'Match must be loaded first' : undefined}
                >
                  Set-Up
                </button>
              </div>

              <div className="schedule-match-actions">
                <button
                  type="button"
                  className="schedule-match-btn schedule-match-btn--next"
                  onClick={() => void handleSave()}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>,
      document.body,
    )
      : null;

  return (
    <>
      {modalLayer}

      <TickerAutomationModal
        open={tickerModalOpen}
        onOpenChange={setTickerModalOpen}
        matchId={draftMatchId}
      />
    </>
  );
}
